import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { set } from 'mobx';
import { ACDLError, ForbiddenError } from '../../../../__legacy__/models/error.model';
import { NotificationLevel, ToastNotification } from '../../../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../../../__legacy__/modules/notifications/services/notifications.service';
import { IImageFilters, ImageDefaultFilters } from '../../../../__legacy__/modules/projectDetails/sub/images/imageFilters.model';
import { ImageAnnotationStatus } from '../../../../__legacy__/modules/projectDetails/sub/images/projectDetailsImages.model';
import { IImageFilterService } from '../../../../__legacy__/modules/projectDetails/sub/images/services/imageFilters.service';
import { IProjectOverviewStore } from './projectOverview.store';
import { IProjectOverviewApiService } from './projectOverviewApi.service';

const getProjectDetailsFailedMessage = 'get_project_details_failed';

export interface IProjectOverviewBl {
  getProjectDetailsAnnotatingAsync: (projectId: string) => Promise<void | ACDLError>;
  getUnclearImagesFilter: (projectId: string) => Promise<void | ACDLError>;

  store: IProjectOverviewStore;
}

export namespace IProjectOverviewBl {
  export const $: interfaces.ServiceIdentifier<IProjectOverviewBl> = Symbol('PROJECT_OVERVIEW_SERVICE');
}

@injectable()
export class ProjectOverviewBl implements IProjectOverviewBl {
  constructor(
    @inject(IProjectOverviewApiService.$) private readonly api: IProjectOverviewApiService,
    @inject(IProjectOverviewStore.$) public readonly store: IProjectOverviewStore,
    @inject(INotificationsService.$) private readonly notifications: INotificationsService,
    @inject(IImageFilterService.$) private readonly imageFilter: IImageFilterService
  ) {}

  getProjectDetailsAnnotatingAsync = async (projectId: string): Promise<void> => {
    const result = await this.api.getProjectOverviewAsync(projectId);
    await this.notifyAboutApiError(result, getProjectDetailsFailedMessage);
    if (result instanceof ACDLError) {
      return;
    }

    set(this.store, result);
  };

  getUnclearImagesFilter = async (projectId: string): Promise<void> => {
    const clarificationFilter: IImageFilters = { ...ImageDefaultFilters, annotationStatuses: [ImageAnnotationStatus.CLARIFICATION] };
    const unclearImagesFilterId = await this.imageFilter.upsertFiltersAsync(projectId, clarificationFilter);

    set(this.store, { unclearImagesFilterId });
  };

  notifyAboutApiError = async (result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> => {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notifications.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  };
}
